import { useState } from 'react';
import useSWR from 'swr';
import { api } from '../lib/api';
import type { SettingsResponse, EnvVariable } from '../types';

function VariableRow({ variable }: { variable: EnvVariable }) {
  const [showValue, setShowValue] = useState(false);

  const displayValue = () => {
    if (variable.value === null || variable.value === undefined) {
      return <span className="text-gray-500 italic">not set</span>;
    }
    if (variable.sensitive && !showValue) {
      return <span className="text-gray-400">••••••••</span>;
    }
    if (typeof variable.value === 'boolean') {
      return variable.value ? 'true' : 'false';
    }
    return String(variable.value);
  };

  return (
    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono text-blue-400">{variable.name}</code>
          {variable.required && (
            <span className="text-xs px-1.5 py-0.5 bg-red-900/50 text-red-400 rounded">
              required
            </span>
          )}
          {variable.sensitive && (
            <span className="text-xs px-1.5 py-0.5 bg-yellow-900/50 text-yellow-400 rounded">
              sensitive
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">{variable.description}</p>
      </td>
      <td className="py-3 px-4 text-center">
        <span className="text-xs text-gray-400">{variable.type}</span>
      </td>
      <td className="py-3 px-4">
        {variable.default !== undefined ? (
          <code className="text-xs text-gray-500">{String(variable.default)}</code>
        ) : (
          <span className="text-gray-600">-</span>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          {variable.isSet ? (
            <span className="w-2 h-2 bg-green-500 rounded-full" title="Set" />
          ) : (
            <span className="w-2 h-2 bg-gray-600 rounded-full" title="Not set" />
          )}
          <code className="text-sm font-mono text-gray-300">{displayValue()}</code>
          {variable.sensitive && variable.isSet && (
            <button
              onClick={() => setShowValue(!showValue)}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              {showValue ? 'hide' : 'show'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export function SettingsPage() {
  const { data, error, isLoading } = useSWR<SettingsResponse>(
    '/api/settings',
    () => api.getSettings(),
    { refreshInterval: 0 } // Don't auto-refresh settings
  );

  if (isLoading) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-8">
          <div className="animate-pulse flex items-center justify-center">
            <span className="text-gray-500">Loading settings...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4">
          <p className="text-red-400">Error loading settings: {error.message}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { categories, schema, validation } = data;

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-1">
            Environment variables for the running system
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {schema.variableCount} variables ({schema.requiredCount} required)
        </div>
      </div>

      {/* Validation Status */}
      {!validation.valid && (
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4">
          <h3 className="text-red-400 font-medium mb-2">
            Configuration Errors ({validation.errorCount})
          </h3>
          <ul className="space-y-1">
            {validation.errors.map((err, i) => (
              <li key={i} className="text-sm text-red-300">
                {err}
              </li>
            ))}
          </ul>
        </div>
      )}

      {validation.warningCount > 0 && (
        <div className="bg-yellow-950/50 border border-yellow-900 rounded-lg p-4">
          <h3 className="text-yellow-400 font-medium mb-2">
            Warnings ({validation.warningCount})
          </h3>
          <ul className="space-y-1">
            {validation.warnings.map((warn, i) => (
              <li key={i} className="text-sm text-yellow-300">
                {warn}
              </li>
            ))}
          </ul>
        </div>
      )}

      {validation.valid && validation.warningCount === 0 && (
        <div className="bg-green-950/50 border border-green-900 rounded-lg p-4">
          <p className="text-green-400">All required environment variables are configured correctly.</p>
        </div>
      )}

      {/* Categories */}
      {schema.categories.map((cat) => {
        const categoryData = categories[cat.id];
        if (!categoryData || categoryData.variables.length === 0) return null;

        return (
          <div
            key={cat.id}
            className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden"
          >
            <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white">{cat.name}</h2>
              <p className="text-sm text-gray-400">{cat.description}</p>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800 text-left">
                  <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase">
                    Variable
                  </th>
                  <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase text-center">
                    Type
                  </th>
                  <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase">
                    Default
                  </th>
                  <th className="py-2 px-4 text-xs font-medium text-gray-500 uppercase">
                    Current Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {categoryData.variables.map((variable) => (
                  <VariableRow key={variable.name} variable={variable} />
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Footer */}
      <div className="text-xs text-gray-600 text-center">
        Last fetched: {new Date(data.timestamp).toLocaleString()}
      </div>
    </div>
  );
}
